/* eslint-disable no-underscore-dangle */
import { useFieldArray } from 'react-hook-form';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';

// Components
import Button from '../../atoms/Button/Button';

// Utils
import cn from '../../../utils/cn';

// Styles
import styles from './RowBatchList.module.css';

/**
 * RowBatchList - List component for batch row actions with react-hook-form integration
 * @param {object} props
 * @param {import('react-hook-form').Control<any>} props.control - React Hook Form control object
 * @param {string} [props.name='actions'] - Field name for the array
 * @param {(params: {field: RowBatchItem, index: number, remove: () => void, count: number, update: (value: any) => void}) => import('react').ReactNode} props.children - Render function called for each field
 * @param {() => RowBatchItem} [props.createNewItem] - Function to create a new item (default returns empty object)
 * @param {string} [props.addButtonLabel] - Label for the add button
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} RowBatchList component
 */
function RowBatchList({
  control,
  name = 'actions',
  children,
  createNewItem,
  addButtonLabel = 'Ajouter une action',
  className,
}) {
  const {
    fields,
    append,
    remove,
    move,
    update,
  } = useFieldArray({
    control,
    name,
    keyName: '_id',
  });
  /** @type {Array<RowBatchItem & { _id: string }>} */
  // @ts-expect-error - useFieldArray adds _id but TypeScript doesn't infer it correctly
  const typedFields = fields;

  /**
   * Drag and Drop sensors management
   */
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 3,
      },
    }),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  /**
   * Handle drag end event
   * @param {import('@dnd-kit/core').DragEndEvent} event
   */
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = typedFields.findIndex((field) => field._id === active.id);
      const newIndex = typedFields.findIndex((field) => field._id === over?.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        move(oldIndex, newIndex);
      }
    }
  };

  /**
   * Handle add item event
   */
  const handleAddItem = () => {
    const newItem = createNewItem();
    append(newItem);
  };

  /**
   * Handle remove item event
   * @param {number} index
   */
  const handleRemoveItem = (index) => {
    if (fields.length === 1) {
      return;
    }
    remove(index);
  };

  return (
    <div className={cn([styles.container, className])}>
      {fields.length > 0 && (
        <div className={styles.list}>
          <DndContext
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            sensors={sensors}
          >
            <SortableContext
              items={typedFields.map((field) => field._id)}
              strategy={verticalListSortingStrategy}
            >
              {typedFields.map((field, index) => (
                <div
                  key={field._id}
                  className={styles.item}
                >
                  {children({
                    field,
                    index,
                    remove: () => handleRemoveItem(index),
                    count: fields.length,
                    update: (value) => {
                      update(index, value);
                    },
                  })}
                </div>
              ))}
            </SortableContext>
          </DndContext>
        </div>
      )}

      <div className={styles.footer}>
        <Button
          color="no-background"
          size="small"
          icon="add"
          onClick={handleAddItem}
        >
          {addButtonLabel}
        </Button>
      </div>
    </div>
  );
}

export default RowBatchList;
